import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const TheInjector = () => {
    const titleRef = useRef(null);
    const containerRef = useRef(null);
    const subtitleRef = useRef(null);
    const scrollPromptRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ delay: 0.3 });
            tl.fromTo(titleRef.current, 
                { y: 80, opacity: 0, filter: 'blur(20px)' },
                { y: 0, opacity: 1, filter: 'blur(0px)', duration: 1.4, ease: 'power3.out' }
            );
            tl.fromTo(subtitleRef.current,
                { y: 30, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.8, ease: 'power2.out' }, '-=0.6'
            );
            tl.fromTo(scrollPromptRef.current,
                { opacity: 0, y: 20 },
                { opacity: 0.8, y: 0, duration: 0.8, ease: 'power2.out' }, '-=0.4'
            );

            gsap.fromTo(titleRef.current,
                { scaleX: 1, opacity: 1, filter: 'blur(0px)' },
                {
                    scaleX: 1.1,
                    opacity: 0,
                    filter: 'blur(10px)',
                    ease: 'power2.in',
                    immediateRender: false,
                    scrollTrigger: {
                        trigger: containerRef.current,
                        start: 'top top',
                        end: '80% top',
                        scrub: 0.1
                    }
                }
            );

            gsap.fromTo(subtitleRef.current,
                { y: 0, opacity: 1 },
                {
                    y: -50,
                    opacity: 0,
                    ease: 'power2.in',
                    immediateRender: false,
                    scrollTrigger: {
                        trigger: containerRef.current,
                        start: 'top top',
                        end: '60% top',
                        scrub: 0.1
                    }
                }
            );

            gsap.fromTo(scrollPromptRef.current,
                { opacity: 0.8 },
                {
                    opacity: 0,
                    ease: 'power2.in',
                    immediateRender: false,
                    scrollTrigger: {
                        trigger: containerRef.current,
                        start: 'top top',
                        end: '30% top',
                        scrub: 0.1
                    }
                }
            );
        });

        return () => ctx.revert();
    }, []);

    return (
        <section ref={containerRef} className="h-[120vh] relative z-10 pointer-events-none">
            <div className="sticky top-0 h-screen flex flex-col items-center justify-center p-6">
                
               <div className="absolute top-8 left-8 hidden md:block">
                   <p className="font-mono text-[10px] text-white/40 uppercase tracking-widest">
                       Built by students in<br/>India, Kazakhstan & Canada
                   </p>
               </div>
               <div className="absolute top-8 right-8 hidden md:block">
                   <p className="font-mono text-[10px] text-white/40 uppercase tracking-widest text-right">
                       Seven Detector Studies
                   </p>
               </div>

                <div className="relative mix-blend-difference text-center">
                    <h1 
                        ref={titleRef}
                        className="font-display font-black text-[12vw] md:text-[8vw] leading-[0.9] tracking-tight text-white uppercase select-none"
                    >
                        Physics you <br/> can touch.
                    </h1>
                </div>
                <div ref={subtitleRef} className="mt-8 text-center max-w-xl mx-auto">
                    <p className="font-light text-xl md:text-2xl text-white/80 leading-relaxed">
                        An interactive toy Monte Carlo for learning particle physics  -  through experimentation, visual feedback, and guided discovery.
                    </p>
                </div>
                
                <div ref={scrollPromptRef} className="absolute bottom-12 text-center pointer-events-auto">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Scroll to explore</p>
                    <div className="flex flex-col items-center gap-1 animate-chevron">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-white/60">
                            <path d="M5 8L10 13L15 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-white/30 -mt-2">
                            <path d="M5 8L10 13L15 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TheInjector;
